// src/components/DetailedFeatureUsage.js
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, subDays, addDays, startOfDay } from 'date-fns';
import { API_BASE_URL } from '../config';

function useDebouncedValue(value, delay = 2000) {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/* ───────── helpers ───────── */
function pageSlice(items, page, pageSize) {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}
function Pagination({ page, pageSize, total, onChange }) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const canPrev = page > 1, canNext = page < pageCount;
  const bar = { display:'flex', gap:8, alignItems:'center', justifyContent:'space-between', padding:12, borderTop:'1px solid #e5e7eb', background:'#fff' };
  const btn = (ok)=>({ padding:'6px 10px', border:'1px solid #e5e7eb', background:ok?'#fff':'#f3f4f6', borderRadius:8, cursor:ok?'pointer':'not-allowed', fontSize:12 });
  const meter = { fontSize:12, color:'#64748b' };
  const select = { padding:'6px 8px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:12 };
  return (
    <div style={bar}>
      <span style={meter}>Page {page} / {pageCount} • {total.toLocaleString()} batches</span>
      <div style={{ display:'flex', gap:8, alignItems:'center' }}>
        <select value={pageSize} onChange={(e)=>onChange({ page:1, pageSize:Number(e.target.value) })} style={select}>
          {[10,25,50,100,250,500].map(n=><option key={n} value={n}>{n} / page</option>)}
        </select>
        <button onClick={()=>onChange({ page:1 })} disabled={!canPrev} style={btn(canPrev)} title="First">«</button>
        <button onClick={()=>onChange({ page:page-1 })} disabled={!canPrev} style={btn(canPrev)} title="Prev">‹</button>
        <button onClick={()=>onChange({ page:page+1 })} disabled={!canNext} style={btn(canNext)} title="Next">›</button>
        <button onClick={()=>onChange({ page:pageCount })} disabled={!canNext} style={btn(canNext)} title="Last">»</button>
      </div>
    </div>
  );
}
function downloadCsv({ headers, rows, filename }) {
  const esc = (v)=> v==null?'':(/[",\n]/.test(String(v))?`"${String(v).replace(/"/g,'""')}"`:String(v));
  const head = headers.map(h=>esc(h.label)).join(',');
  const body = rows.map(r=>headers.map(h=>esc(h.get?h.get(r):r[h.key])).join(','));
  const csv = [head, ...body].join('\n');
  const blob = new Blob([csv], { type:'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob); const a = document.createElement('a');
  a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
}

/* ───────── component ───────── */
const defaultStartDate = format(subDays(startOfDay(new Date()), 30), 'yyyy-MM-dd');
const defaultEndDate = format(startOfDay(addDays(new Date(), 1)), 'yyyy-MM-dd');

export default function DetailedFeatureUsage() {
  const [filters, setFilters] = useState({
    userEmail: '', licenceId: '', companyId: '', projectId: '',
    startDate: defaultStartDate, endDate: defaultEndDate,
  });
  const [featureText, setFeatureText] = useState(''); // NEW quick text filter for features
  const debouncedFilters = useDebouncedValue(filters, 2000);
  const [detailedData, setDetailedData] = useState([]);
  const [detailedError, setDetailedError] = useState(null);
  const [loading, setLoading] = useState(false);

  const [sortConfig] = useState({ key: 'dateUtc', direction: 'desc' });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // expand / collapse per batch
  const [expanded, setExpanded] = useState(()=> new Set()); // store batch ids
  const isExpanded = (id) => expanded.has(id);
  const toggleExpand = (id) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Auto-paginate fetch: 1000 per chunk using Skip
  const fetchDetailedData = useCallback(async () => {
    setLoading(true);
    setDetailedError(null);
    try {
      const CHUNK = 1000;
      let skip = 0;
      let all = [];
      let total = Infinity;

      const baseParams = {
        userEmail: debouncedFilters.userEmail || undefined,
        licenceId: debouncedFilters.licenceId || undefined,
        companyId: debouncedFilters.companyId || undefined,
        projectId: debouncedFilters.projectId || undefined,
        startDate: debouncedFilters.startDate || undefined,
        endDate: debouncedFilters.endDate || undefined,
      };

      while (all.length < total) {
        const params = { ...baseParams, Skip: skip, Take: CHUNK, PageSize: CHUNK, Limit: CHUNK };
        const res = await axios.get(`${API_BASE_URL}/api/usage`, { params });
        const payload = res.data;

        let items = [];
        if (Array.isArray(payload)) items = payload;
        else if (Array.isArray(payload.items)) items = payload.items;
        else if (Array.isArray(payload.usage)) items = payload.usage;
        else if (Array.isArray(payload.data)) items = payload.data;
        else if (Array.isArray(payload.results)) items = payload.results;
        else if (Array.isArray(payload.featureUsageBatches)) items = payload.featureUsageBatches;

        const reportedTotal =
          payload?.total ??
          payload?.totalCount ??
          payload?.count ??
          (payload?.meta?.total ?? null);

        if (typeof reportedTotal === 'number' && isFinite(reportedTotal)) {
          total = reportedTotal;
        } else if (!isFinite(total)) {
          total = Number.MAX_SAFE_INTEGER;
        }

        all = all.concat(items || []);
        if (!items || items.length < CHUNK) break;
        skip += CHUNK;
      }

      setDetailedData(all);
      setExpanded(new Set()); // collapse after new fetch
    } catch (err) {
      setDetailedError(err?.response?.data?.message || err.message || 'Failed to load usage');
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters]);

  useEffect(() => { fetchDetailedData(); }, [fetchDetailedData]);
  useEffect(() => { setPage(1); }, [filters, sortConfig, featureText]);

  // Normalise
  const normalized = useMemo(() => {
    return (detailedData || []).map(b => {
      const featureUsages = Array.isArray(b.featureUsages) ? b.featureUsages
        : (Array.isArray(b.features) ? b.features
        : (Array.isArray(b.usage) ? b.usage : []));
      const featuresCell = featureUsages
        .map(f => `${(f.featureName ?? f.name ?? f.key ?? '').trim()}: ${Number(f.usageCount ?? f.count ?? f.value ?? 0)}`)
        .join(' | ');
      return {
        id: b.id ?? b.batchId ?? b.batchID ?? b._id ?? null,
        userEmail: b.userEmail ?? b.email ?? '',
        dateUtc: b.dateUtc ?? b.createdUtc ?? b.createdAt ?? b.timestamp ?? null,
        licenceId: b.licenceId ?? b.licenseId ?? b.licenceID ?? null,
        companyId: b.companyId ?? b.companyID ?? null,
        projectId: b.projectId ?? b.projectID ?? null,
        diagnosticInfo: b.diagnosticInfo ?? b.notes ?? '',
        totalCount: b.totalCount ?? b.total ?? null,
        resultCount: b.resultCount ?? b.succeeded ?? null,
        skipped: b.skipped ?? b.failures ?? null,
        featureUsages: (featureUsages || []).map(f => ({
          featureName: f.featureName ?? f.name ?? f.key ?? '',
          usageCount: f.usageCount ?? f.count ?? f.value ?? 0,
        })),
        featuresCell, // NEW for CSV
        _raw: b
      };
    });
  }, [detailedData]);

  // Client filtering (with feature text contains)
  const filtered = useMemo(() => {
    const e = (filters.userEmail || '').toLowerCase();
    const ftxt = (featureText || '').toLowerCase();
    const sDate = filters.startDate ? new Date(filters.startDate + 'T00:00:00') : null;
    const eDate = filters.endDate ? new Date(filters.endDate + 'T23:59:59') : null;

    return normalized.filter(b => {
      if (e && !(b.userEmail || '').toLowerCase().includes(e)) return false;
      if (filters.licenceId && !String(b.licenceId ?? '').includes(filters.licenceId)) return false;
      if (filters.companyId && !String(b.companyId ?? '').includes(filters.companyId)) return false;
      if (filters.projectId && !String(b.projectId ?? '').includes(filters.projectId)) return false;

      const t = b.dateUtc ? new Date(b.dateUtc) : null;
      if (sDate && t && t < sDate) return false;
      if (eDate && t && t > eDate) return false;

      if (ftxt) {
        const hit = (b.featureUsages || []).some(f =>
          (f.featureName || '').toLowerCase().includes(ftxt)
        );
        if (!hit) return false;
      }
      return true;
    });
  }, [normalized, filters, featureText]);

  // Sorting
  const [sortCfg, setSortCfg] = useState({ key:'dateUtc', direction:'desc' });
  useEffect(()=>{ setSortCfg(sortConfig); }, [sortConfig]); // keep external name
  const sortedDetailed = useMemo(() => {
    const arr = [...filtered];
    const { key, direction } = sortCfg;
    arr.sort((a, b) => {
      const av = a?.[key]; const bv = b?.[key];
      let cmp;
      if (/date/i.test(key)) {
        const da = av ? new Date(av).getTime() : 0;
        const db = bv ? new Date(bv).getTime() : 0;
        cmp = da - db;
      } else if (typeof av === 'number' || typeof bv === 'number') {
        cmp = (Number(av) || 0) - (Number(bv) || 0);
      } else {
        cmp = String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true, sensitivity: 'base' });
      }
      return direction === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortCfg]);

  const currentPageBatches = pageSlice(sortedDetailed, page, pageSize);

  // CSV (batch-level; includes removed columns too so “all info” is preserved)
  const csvHeaders = [
    { key:'id', label:'Batch ID' },
    { key:'userEmail', label:'User Email' },
    { key:'dateLocal', label:'Date (Local)', get: r => r.dateUtc ? new Date(r.dateUtc).toLocaleString() : '' },
    { key:'licenceId', label:'Licence ID' },
    { key:'companyId', label:'Company ID' },
    { key:'projectId', label:'Project ID' },
    { key:'diagnosticInfo', label:'Diagnostic Info' },
    { key:'totalCount', label:'Batch Total' },      // included only in CSV
    { key:'resultCount', label:'Batch Result' },   // included only in CSV
    { key:'skipped', label:'Batch Skipped' },      // included only in CSV
    { key:'featuresCell', label:'Features' },      // NEW: single cell with detailed features
  ];
  const exportCurrent = () =>
    downloadCsv({ headers: csvHeaders, rows: currentPageBatches, filename:`detailed_feature_usage_page-${page}.csv` });
  const exportAll = () =>
    downloadCsv({ headers: csvHeaders, rows: sortedDetailed, filename:`detailed_feature_usage_all.csv` });

  /* ───────── UI ───────── */
  const shell = { background:'#f8fafc', minHeight:'100%', padding:16 };
  const card = { background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, overflow:'hidden' };
  const titleRow = { display:'flex', alignItems:'center', justifyContent:'space-between', padding:12, borderBottom:'1px solid #e5e7eb' };
  const filterBar = { display:'flex', flexWrap:'wrap', gap:8, alignItems:'center', padding:12, borderBottom:'1px solid #e5e7eb' };
  const input = { padding:'8px 10px', borderRadius:10, border:'1px solid #e5e7eb', background:'#fff', fontSize:12, outline:'none' };
  const btn = { padding:'8px 12px', borderRadius:10, fontSize:12, fontWeight:700, cursor:'pointer' };
  const btnGhost = { ...btn, background:'#fff', color:'#0f172a', border:'1px solid #e5e7eb' };
  const btnPrimary = { ...btn, background:'#0b0f19', color:'#fff', border:'1px solid #0b0f19' };
  const tableWrap = { overflowX:'auto' };
  const table = { width:'100%', borderCollapse:'collapse', fontSize:12 };
  const th = { background:'#f6f7fb', textAlign:'left', padding:10, fontWeight:700, borderBottom:'1px solid #e5e7eb', whiteSpace:'nowrap' };
  const td = { padding:10, borderBottom:'1px solid #e5e7eb', verticalAlign:'top' };

  // NOTE: removed total/result/skipped columns from visible table
  const batchHeaders = [
    { key:'_exp', label:'' }, // expand button
    { key:'dateUtc', label:'Date' },
    { key:'userEmail', label:'User Email' },
    { key:'licenceId', label:'Licence ID' },
    { key:'companyId', label:'Company ID' },
    { key:'projectId', label:'Project ID' },
    { key:'diagnosticInfo', label:'Diagnostic Info' },
    { key:'id', label:'Batch ID' },
  ];

  const handleSort = (key) =>
    setSortCfg(s => ({ key, direction: s.key === key && s.direction === 'asc' ? 'desc' : 'asc' }));

  return (
    <div style={shell}>
      <div style={card}>
        {/* Title + CSV */}
        <div style={titleRow}>
          <h2 style={{ fontSize:16, margin:0 }}>Detailed Feature Usage</h2>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={exportCurrent} style={btnGhost}>Export current page</button>
            <button onClick={exportAll} style={btnPrimary}>Export all pages</button>
          </div>
        </div>

        {/* Filters */}
        <div style={filterBar}>
          <label style={{ fontSize:12, color:'#64748b' }}>
            Start
            <input
              type="date"
              value={filters.startDate}
              onChange={(e)=>setFilters(f=>({ ...f, startDate: e.target.value }))}
              style={{ ...input, marginLeft:6 }}
            />
          </label>
          <label style={{ fontSize:12, color:'#64748b' }}>
            End
            <input
              type="date"
              value={filters.endDate}
              max={format(startOfDay(addDays(new Date(), 1)), 'yyyy-MM-dd')}
              onChange={(e)=>setFilters(f=>({ ...f, endDate: e.target.value }))}
              style={{ ...input, marginLeft:6 }}
            />
          </label>
          <label style={{ fontSize:12, color:'#64748b' }}>
            Email
            <input
              type="text"
              placeholder="user@company.com"
              value={filters.userEmail}
              onChange={(e)=>setFilters(f=>({ ...f, userEmail: e.target.value }))}
              style={{ ...input, marginLeft:6, minWidth:220 }}
            />
          </label>
          <label style={{ fontSize:12, color:'#64748b' }}>
            Licence ID
            <input
              type="text"
              value={filters.licenceId}
              onChange={(e)=>setFilters(f=>({ ...f, licenceId: e.target.value }))}
              style={{ ...input, marginLeft:6, width:140 }}
            />
          </label>
          <label style={{ fontSize:12, color:'#64748b' }}>
            Company ID
            <input
              type="text"
              value={filters.companyId}
              onChange={(e)=>setFilters(f=>({ ...f, companyId: e.target.value }))}
              style={{ ...input, marginLeft:6, width:140 }}
            />
          </label>
          <label style={{ fontSize:12, color:'#64748b' }}>
            Project ID
            <input
              type="text"
              value={filters.projectId}
              onChange={(e)=>setFilters(f=>({ ...f, projectId: e.target.value }))}
              style={{ ...input, marginLeft:6, width:140 }}
            />
          </label>

          {/* NEW feature text contains filter */}
          <label style={{ fontSize:12, color:'#64748b' }}>
            Feature contains
            <input
              type="text"
              placeholder="e.g. getinspectionusers"
              value={featureText}
              onChange={(e)=>setFeatureText(e.target.value)}
              style={{ ...input, marginLeft:6, minWidth:220 }}
              title="Filters batches to those with any feature whose name contains this text"
            />
          </label>

          <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
            <button onClick={fetchDetailedData} style={btnGhost}>Refresh</button>
            <button
              onClick={() => { 
                setFilters({ userEmail:'', licenceId:'', companyId:'', projectId:'', startDate: defaultStartDate, endDate: defaultEndDate });
                setFeatureText('');
              }}
              style={btnGhost}
            >
              Clear
            </button>
          </div>
        </div>

        {/* Table */}
        <div style={tableWrap}>
          <table style={table}>
            <thead>
              <tr>
                {batchHeaders.map(h => (
                  <th
                    key={h.key}
                    onClick={h.key !== '_exp' ? (() => handleSort(h.key)) : undefined}
                    style={{ ...th, cursor: h.key !== '_exp' ? 'pointer' : 'default', width: h.key === '_exp' ? 44 : undefined }}
                  >
                    {h.label}{sortCfg.key === h.key ? (sortCfg.direction === 'asc' ? ' ↑' : ' ↓') : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={batchHeaders.length} style={{ padding:16, color:'#64748b' }}>Loading…</td></tr>
              ) : detailedError ? (
                <tr><td colSpan={batchHeaders.length} style={{ padding:16, color:'#b91c1c' }}>{detailedError}</td></tr>
              ) : sortedDetailed.length === 0 ? (
                <tr><td colSpan={batchHeaders.length} style={{ padding:16, color:'#64748b' }}>No detailed data</td></tr>
              ) : (
                currentPageBatches.map((b, idx) => (
                  <React.Fragment key={(b.id ?? 'batch') + '_' + idx}>
                    {/* Batch summary row */}
                    <tr>
                      <td style={{ ...td, textAlign:'center' }}>
                        <button
                          onClick={() => toggleExpand(b.id ?? idx)}
                          style={{ padding:'4px 8px', border:'1px solid #e5e7eb', borderRadius:8, background:'#fff', cursor:'pointer' }}
                          title={isExpanded(b.id ?? idx) ? 'Collapse' : 'Expand'}
                        >
                          {isExpanded(b.id ?? idx) ? '−' : '+'}
                        </button>
                      </td>
                      <td style={{ ...td, whiteSpace:'nowrap' }}>{b.dateUtc ? new Date(b.dateUtc).toLocaleString() : '—'}</td>
                      <td style={{ ...td, maxWidth:220, overflow:'hidden', textOverflow:'ellipsis' }}>{b.userEmail || '—'}</td>
                      <td style={td}>{b.licenceId || '—'}</td>
                      <td style={td}>{b.companyId || '—'}</td>
                      <td style={td}>{b.projectId || '—'}</td>
                      <td style={{ ...td, maxWidth:320, overflow:'hidden', textOverflow:'ellipsis' }} title={b.diagnosticInfo}>
                        {b.diagnosticInfo || '—'}
                      </td>
                      <td style={td}>{b.id || '—'}</td>
                    </tr>

                    {/* Feature rows (collapsed by default, expand on click) */}
                    {isExpanded(b.id ?? idx) && (b.featureUsages || []).map((fu, i) => (
                      <tr key={'fu_' + i} style={{ background:'#fafafa' }}>
                        <td></td>
                        <td colSpan={2} style={{ paddingLeft: 24, color: '#666' }}>└ {fu.featureName || '—'}</td>
                        <td style={{ fontWeight: 600 }}>{fu.usageCount ?? 0}</td>
                        <td colSpan={4}></td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page}
          pageSize={pageSize}
          total={sortedDetailed.length}
          onChange={({ page: p = page, pageSize: ps = pageSize }) => { setPage(p); setPageSize(ps); }}
        />
      </div>
    </div>
  );
}
